<script lang="ts">
  import { onMount } from 'svelte';
  import Table from '../../Table.svelte';
  import { push } from 'svelte-spa-router';
  import { DonorService, type Donor, type DonationHistoryItem } from '../../../lib/api/charityServices';
  import { formatDate } from '../../../lib/utils/formatters';
  import BackButton from '../../BackButton.svelte';
  export let donorType: string;
  export let donorId: string;

  // State management
  let loading = true;
  let error: string | null = null;
  let donorProfile: Donor | null = null;
  let donationHistory: DonationHistoryItem[] = [];
  let historyLoading = true;
  let historyError: string | null = null;

  // Transform donation history data for table display
  function transformDonationHistoryForTable(donations: DonationHistoryItem[]) {
    return donations.map(donation => ({
      employee: {
        name: donation.donor?.name || 'N/A',
        email: donation.donor?.email || 'N/A'
      },
      hours: donation.leaveHours,
      employeeTotal: donation.leaveValueCents,
      employerContribution: donation.employerTopUpCents || 0,
      total: donation.totalValueCents,
      approvedDate: donation.approvedAt
    }));
  }

  // Table columns configuration
  const columns = [
    {
      key: 'employee',
      label: 'Employee',
      width: '20%',
      render: (value: { name: string, email: string }) => `
        <div class="employee-info">
          <span class="employee-name">${value.name}</span>
          <span class="employee-email" title="Click to copy email">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
              <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H4V8l8 5 8-5v10zm-8-7L4 6h16l-8 5z"/>
            </svg>
            <span class="email-text">${value.email}</span>
          </span>
        </div>`
    },
    {
      key: 'hours',
      label: 'Hours',
      width: '10%',
      render: (value: number) => `<span class="hours-value">${value.toString().padStart(2, '0')} hrs</span>`
    },
    {
      key: 'employeeTotal',
      label: 'Employee Total',
      width: '15%',
      render: (value: number) => `<span class="amount-value">${formatCurrency(value)}</span>`
    },
    {
      key: 'employerContribution',
      label: 'Employer Contribution',
      width: '15%',
      render: (value: number) => `<span class="amount-value">${formatCurrency(value)}</span>`
    },
    {
      key: 'total',
      label: 'Total',
      width: '15%',
      render: (value: number) => `<span class="amount-value total-value">${formatCurrency(value)}</span>`
    },
    {
      key: 'approvedDate',
      label: 'Date',
      width: '15%',
      render: (value: string) => `<span class="date-value">${formatDate(value)}</span>`
    }
  ];

  // Format currency from cents to dollars
  function formatCurrency(cents: number): string {
    if (cents === undefined || cents === null) return '$0.00';
    
    const dollars = cents / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(dollars);
  }


  // Load donor profile
  async function loadDonorProfile() {
    try {
      const result = await DonorService.getDonor(donorType, donorId);
      
      if (result.success && result.donor) {
        donorProfile = result.donor;
      } else {
        error = result.error || 'Failed to load donor profile';
      }
    } catch (err) {
      console.error('Error loading donor profile:', err);
      error = 'An error occurred while loading donor profile';
    } finally {
      loading = false;
    }
  }

  // Load donation history
  async function loadDonationHistory() {
    try {
      const result = await DonorService.getDonationHistory(donorType, donorId);
      
      if (result.success && result.donations) {
        donationHistory = result.donations;
      } else {
        historyError = result.error || 'Failed to load donation history';
      }
    } catch (err) {
      console.error('Error loading donation history:', err);
      historyError = 'An error occurred while loading donation history';
    } finally {
      historyLoading = false;
    }
  }

  function handleBackClick() {
    push('/donors');
  }

  // Add click handler for copying email
  function setupEmailCopyHandlers() {
    document.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      if (target.closest('.employee-email')) {
        const emailText = target.closest('.employee-info')?.querySelector('.email-text')?.textContent;
        if (emailText) {
          navigator.clipboard.writeText(emailText)
            .then(() => {
              showNotification('Email copied to clipboard!', 'success');
            })
            .catch(() => {
              showNotification('Failed to copy email', 'error');
            });
        }
      }
    });
  }

  // Add notification state
  let showingNotification = false;
  let notificationMessage = '';
  let notificationTimeout: number;

  function showNotification(message: string, type: 'success' | 'error' = 'success') {
    notificationMessage = message;
    showingNotification = true;
    
    if (notificationTimeout) {
      clearTimeout(notificationTimeout);
    }
    
    notificationTimeout = setTimeout(() => {
      showingNotification = false;
    }, 3000) as unknown as number;
  }

  onMount(() => {
    loadDonorProfile();
    loadDonationHistory();
    setupEmailCopyHandlers();

    return () => {
      if (notificationTimeout) {
        clearTimeout(notificationTimeout);
      }
    };
  });
</script>

<!-- Add notification component -->
{#if showingNotification}
  <div class="notification" class:success={notificationMessage.includes('copied')}>
    {notificationMessage}
  </div>
{/if}

<div class="donor-detail-container">
  <div class="back-button-container">
    <BackButton text="Back" onClick={() => window.history.back()} />
  </div>

  {#if loading}
    <div class="loading-container">
      <div class="loading-spinner"></div>
      <p>Loading donor profile...</p>
    </div>
  {:else if error}
    <div class="error-container">
      <p class="error-message">{error}</p>
      <button class="retry-button" on:click={loadDonorProfile}>
        Try Again
      </button>
    </div>
  {:else if donorProfile}
    <!-- Profile Information -->
    <div class="profile-section">
      <div class="profile-grid">
        <div class="profile-box">
          <div class="box-label">Employer</div>
          <div class="box-content">
            <div class="box-value">{donorProfile.donorName}</div>
          </div>
        </div>
        
        <div class="profile-box">
          <div class="box-label">Total</div>
          <div class="box-content">
            <div class="box-value">{formatCurrency(donorProfile.totalValueCents)}</div>
          </div>
        </div>
        
        <div class="profile-box">
          <div class="box-label">Status</div>
          <div class="box-content">
            <div class="status-badge paid">Paid</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Donation History -->
    <div class="history-section">
      <h3 class="section-title">Donation History</h3>
      <div class="table-container">
        {#if historyLoading}
          <div class="loading-container">
            <div class="loading-spinner"></div>
            <p>Loading donation history...</p>
          </div>
        {:else if historyError}
          <div class="error-container">
            <p class="error-message">{historyError}</p>
            <button class="retry-button" on:click={loadDonationHistory}>
              Try Again
            </button>
          </div>
        {:else if donationHistory.length === 0}
          <div class="empty-container">
            <p>No donation history found</p>
          </div>
        {:else}
          <Table {columns} data={transformDonationHistoryForTable(donationHistory)} />
        {/if}
      </div>
    </div>
  {/if}
</div>

<style>
  .donor-detail-container {
    padding: 2rem;
    background-color: #f7fafc;
    min-height: 100vh;
  }

  .back-button-container {
    margin-bottom: 0;
  }

  .profile-section {
    margin-bottom: 2rem;
  }

  .profile-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1.5rem;
  }

  .profile-box {
    background: white;
    border-radius: 12px;
    padding: 1.5rem;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .box-label {
    font-size: 0.875rem;
    color: #6B7280;
    /* font-weight: 500;F */
    text-transform: uppercase;
    letter-spacing: 0.025em;
  }

  .box-content {
    display: flex;
    align-items: center;
    justify-content: flex-start;
  }

  .box-value {
    font-size: 1.5rem;
    color: #111827;
    font-weight: 500 !important;
    line-height: 1.2;
  }

  .status-badge.paid {
    display: inline-flex;
    align-items: center;
    padding: 0.5rem 1rem;
    background-color: #E6F4EA;
    color: #34A853;
    border-radius: 9999px;
    font-size: 1rem;
    font-weight: 500 ;
  }

  .history-section {
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
    padding: 1.5rem 2rem;
  }

  .section-title {
    font-size: 1.25rem;
    font-weight: 600;
    color: #111827;
    margin: 0 0 1.5rem 0;
  }

  .table-container {
    overflow: hidden;
    border-radius: 8px;
  }

  /* Global styles for table content */
  :global(.date-value) {
    color: #111827;
    font-size: 0.875rem;
  }

  :global(.hours-value) {
    color: #111827;
    font-size: 0.875rem;
  }

  :global(.amount-value) {
    color: #111827;
    font-size: 0.875rem;
    font-weight: 400;
  }

  :global(.total-value) {
    font-weight: 700;
  }

  /* Loading, Error, and Empty States */
  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem;
    text-align: center;
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .loading-spinner {
    width: 2rem;
    height: 2rem;
    border: 3px solid #f3f4f6;
    border-top: 3px solid #f0b429;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 1rem;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  .error-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem;
    text-align: center;
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .error-message {
    color: #dc2626;
    font-size: 1.1rem;
    margin-bottom: 1rem;
  }

  .retry-button {
    background: #f0b429;
    color: white;
    border: none;
    border-radius: 8px;
    padding: 0.75rem 1.5rem;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.2s;
  }

  .retry-button:hover {
    background: #d69e20;
  }

  .empty-container {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 3rem;
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    color: #6b7280;
  }

  :global(.employee-name) {
    color: #111827;
    font-size: 0.875rem;
    font-weight: 500;
  }

  :global(.employee-email) {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    color: #6B7280;
    font-size: 0.75rem;
    cursor: pointer;
    padding: 0.125rem 0;
    transition: color 0.2s;
  }

  :global(.employee-email:hover) {
    color: var(--color-primary);
  }

  :global(.employee-email svg) {
    width: 14px;
    height: 14px;
    opacity: 0.7;
  }

  :global(.email-text) {
    font-size: 0.75rem;
  }

  .notification {
    position: fixed;
    bottom: 2rem;
    right: 2rem;
    background-color: #FEF3C7;
    color: #92400E;
    padding: 1rem 1.5rem;
    border-radius: 0.5rem;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    z-index: 50;
    animation: slideIn 0.2s ease-out;
  }

  .notification.success {
    background-color: #DEF7EC;
    color: #03543F;
  }

  @keyframes slideIn {
    from {
      transform: translateY(1rem);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  /* Responsive Design */
  @media (max-width: 1024px) {
    .profile-grid {
      grid-template-columns: repeat(2, 1fr);
    }

    .profile-box:last-child {
      grid-column: 1 / -1;
    }
  }

  @media (max-width: 640px) {
    .profile-grid {
      grid-template-columns: 1fr;
      gap: 1rem;
    }

    .profile-box {
      padding: 1.25rem;
    }

    .box-value {
      font-size: 1.25rem;
    }

    .status-badge.paid {
      font-size: 0.875rem;
      padding: 0.375rem 0.75rem;

    }
  }
</style> 